import {
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputAdornment,
	InputLabel,
	ListItemText,
	MenuItem,
	Select,
	Snackbar,
	TextField,
	Typography
} from '@material-ui/core'
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles'
import MuiAlert from '@material-ui/lab/Alert'
import moment from 'moment'
import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { deleteAddOn, deliverAddOnForQa, postAddOns, updateAddOn } from '../../actions/addons/addons.actions'
import { selectChip } from '../../services/theme'
import { GROOMING, TRAINING } from '../../utils'
import { isNonProductionEnvironment } from '../../utils/environment'

const useStyles = makeStyles((theme) => ({
	currencyAdornment: {
		marginLeft: '10px',
		marginTop: '7px !important'
	},
	dialog: {
		'& .MuiDialog-paper': {
			maxWidth: '920px'
		}
	},
	formWrapper: {
		display: 'flex',
		flexWrap: 'wrap',
		margin: '24px 0px'
	},
	fieldWrapper: {
		'&:nth-child(odd)': {
			marginRight: '25px'
		},
		display: 'flex',
		flexDirection: 'column',
		marginBottom: '20px'
	},
	dialogActions: {
		justifyContent: 'space-between'
	},
	actionsWrapper: {
		display: 'flex',
		'& .MuiButton-root:nth-child(n + 2)': {
			marginLeft: '16px'
		}
	},
	chip: {
		marginRight: 2
	},
	deleteButton: {
		color: '#D32F2F',
		borderColor: '#D32F2F',
		'&:hover': {
			color: '#FFFFFF',
			backgroundColor: '#D32F2F',
			borderColor: '#D32F2F'
		}
	}
}))

const Alert = (props) => {
	return <MuiAlert elevation={6} variant="filled" {...props} />
}

const EditAddOnDialog = (props) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const facility = useSelector((state) => state.specific.facilities.selected)
	const staff = useSelector((state) => state.specific.team.staffList)
	const visit = useSelector((state) => state.specific.visits.selected)
	const timeBlocks = ['morning', 'midday', 'evening']

	const { isShown, setIsShown, setSelectedAddOn, addOn, isDeleteAllowed } = props

	const [priceChanged, setPriceChanged] = useState(false)
	const [addOnType, setAddOnType] = useState(null)
	const [treat, setTreat] = useState(null)
	const [pet, setPet] = useState(null)
	const [timeBlock, setTimeBlock] = useState(visit && visit.type === 'daycare' ? 'midday' : null)
	const [values, setValues] = useState((0).toFixed(2))
	const [staff_id, setStaff] = useState('')

	// Errors
	const [treatError, setTreatError] = useState(false)
	const [timeBlockError, setTimeBlockError] = useState(false)
	const [priceError, setPriceError] = useState(false)

	const [hasFormErrors, setHasFormErrors] = useState(false)
	const [isErrorOpen, setErrorOpen] = useState(false)
	const [error, setError] = useState(false)

	const onlyNumbers = (value) => {
		if (!value || value === '0') return value
		const onlyNums = value.toString().replace(/[^\d]/g, '').replace(/^0+/, '')
		if (onlyNums.length) return onlyNums
		return 0
	}

	const formatNumsToDollars = (value) => {
		if (value !== undefined && value !== null) {
			return `${(value / 100).toFixed(2)}`
		}
		return ''
	}

	useEffect(() => {
		if (facility && addOn) {
			// Meals often have category but no type — still hydrate dialog state for QA deliver.
			if (addOn.type || addOn.category === 'meal') {
				let facilityAddOn = addOn.type
					? (facility.add_on_services[addOn.type] || [])?.find(
							(facilityAddOn) => facilityAddOn._id === addOn.addon_id
					  )
					: null

				setAddOnType(addOn)

				if (addOn.type === 'treat') {
					setTreat(facilityAddOn)
				}

				if (addOn.pet_id && visit) {
					let addOnPet = visit.pets.find((pet) => pet._id === addOn.pet_id)

					setPet(addOnPet)
				}

				if (addOn.time) {
					setTimeBlock(addOn.time)
				}

				if (addOn.staff_id) setStaff(addOn.staff_id)

				// setting the price

				let price = 0
				if ('price' in addOn) {
					price = addOn.price
				} else {
					price = facilityAddOn?.price || 0

					if (addOn.addon_choice_id && facilityAddOn && !('price' in addOn)) {
						let choice = facilityAddOn.addon_choices.find((ac) => ac.id === addOn.addon_choice_id)
						let base = facilityAddOn.price || 0
						let upcharge = choice?.upcharge || 0
						let total = base + upcharge
						price = total
					}
				}

				setValues({
					price: price
				})
			}
		}
	}, [props.addOn])

	const saveAddOn = () => {
		let errors = validateEditAddOnForm()
		setHasFormErrors(errors)

		let addOnUpdate
		let facilityAddOn = facility.add_on_services[addOn.type].find(
			(facilityAddOn) => facilityAddOn._id === addOn.addon_id
		)

		if (errors) {
			showErrorMessage('Please fix form errors.')
		} else {
			addOnUpdate = {
				time: timeBlock,
				staff_id
				// price: values.price * 100,
			}

			if (priceChanged) addOnUpdate.price = onlyNumbers(values.price)

			if (facilityAddOn._id !== addOn.addon_id) {
				addOnUpdate.addon_id = facilityAddOn._id
			}

			dispatch(updateAddOn(addOn._id, addOnUpdate)).then(() => {
				hideDialog()
			})
		}
	}

	const removeAddOn = () => {
		if (isDeleteAllowed && !isDeleteAllowed(addOn)) {
			return
		}

		if (!addOn._id) {
			const skippedAddOn = { ...addOn, pet_ids: [addOn?.pet_id], skipped: true }
			dispatch(postAddOns(visit._id, skippedAddOn)).then(() => {
				hideDialog()
			})
			return
		}

		dispatch(deleteAddOn(addOn._id))
			.then(() => {
				hideDialog()
			})
			.catch(() => {
				// error toast already shown by deleteAddOn
			})
	}

	const deliverEnrichment = () => {
		if (addOn.completed) return
		if (addOn.status === 'delivered' || addOn.status === 'completed') return
		if (!addOn?._id && addOn?.category !== 'meal') return
		dispatch(deliverAddOnForQa(addOn, visit?._id)).then(() => {
			hideDialog()
		})
	}

	const canDeliverForQa =
		isNonProductionEnvironment() &&
		!addOn.completed &&
		addOn.status !== 'delivered' &&
		addOn.status !== 'completed' &&
		(!!addOn?._id || addOn?.category === 'meal')

	const updateTreat = (value) => {
		setTreat(value)

		if (value) {
			let matchingTreat =
				facility.add_on_services['treat'].length > 0
					? facility.add_on_services['treat'].find((treat) => treat.name === value.name)
					: null

			if (matchingTreat) {
				setValues({
					price: matchingTreat.price ? matchingTreat.price / 100 : 0
				})
			}
		}
	}

	const validateEditAddOnForm = () => {
		if (addOnType && addOnType === 'treat') {
			setTreatError(Boolean(!treat))
		}

		setTimeBlockError(Boolean(!timeBlock))
		setPriceError(Boolean(!values.price))

		return !addOnType || (addOnType && addOnType === 'treat' && !treat) || !timeBlock || !values.price
	}

	const showErrorMessage = (error) => {
		setError(error)
		setErrorOpen(true)
	}

	const handleCloseError = (reason) => {
		if (reason === 'clickaway') {
			return
		}

		setErrorOpen(false)
	}

	const renderSelectedAddOns = () => {
		let addOns = facility.add_on_services[addOnType]

		return addOns && addOns.length > 0
			? addOns.map((addOn) => (
					<MenuItem key={addOn._id} value={addOn}>
						<Typography style={{ textTransform: 'capitalize' }}>{addOn.name}</Typography>
					</MenuItem>
			  ))
			: null
	}

	const hideDialog = () => {
		clearForm()
		handleCloseError()
		clearFormErrors()
		setSelectedAddOn(null)
		setPriceChanged(false)
		setIsShown(false)
	}

	const clearForm = () => {
		setAddOnType(null)
		setTreat(null)
		setPet(null)
		setTimeBlock(null)
		setValues({ price: null })
		setPriceChanged(false)
		clearFormErrors()
	}

	const clearFormErrors = () => {
		setTreatError(false)
		setTimeBlockError(false)
		setPriceError(false)
		setPriceChanged(false)
		setHasFormErrors(false)
	}

	const updatePrice = (val) => {
		setValues({
			...values,
			price: val
		})
	}

	const updateStaff = (val) => {
		setStaff(val)
	}

	useEffect(() => {
		return () => {
			clearForm()
		}
	}, [isShown])

	useEffect(() => {
		if (hasFormErrors) {
			validateEditAddOnForm()
		}
	})

	const addon_info = useMemo(() => {
		if (!props.addOn || !facility) return 'what'
		let type = facility?.addOnTypes?.find((a) => a._id == props.addOn.addon_id)
		return type || 'no'
	}, [props.addOn, facility])

	const revenue_type = addon_info?.revenue_type

	const assigned_staff =
		revenue_type && staff?.length
			? staff.filter((s) => {
					if (revenue_type === GROOMING)
						return s?.position?.toLowerCase()?.includes('groom') && !!s.active
					if (revenue_type === TRAINING)
						return s?.position?.toLowerCase()?.includes('train') && !!s.active
					return true
			  })
			: []

	assigned_staff.sort((a, b) => {
		if (a.contact.last_name > b.contact.last_name) return 1
		if (a.contact.last_name < b.contact.last_name) return -1
		return 0
	})

	return (
		<Dialog
			open={isShown}
			onClose={() => hideDialog()}
			aria-labelledby="form-dialog-title"
			className={classes.dialog}>
			<DialogTitle>
				<Typography
					style={{
						marginBottom: '16px',
						fontSize: '1.4063rem',
						fontWeight: 700
					}}>
					Edit Activity
				</Typography>
				<Typography variant="overline">Date of Activity</Typography>
				{addOn.date ? (
					<Typography style={{ fontSize: '1.6867rem', fontWeight: 700 }}>
						{moment(addOn.date).format('ll')}
					</Typography>
				) : null}
			</DialogTitle>
			<DialogContent>
				<div className={classes.formWrapper}>
					{addOnType ? (
						<div className={classes.fieldWrapper}>
							<Typography variant="overline">Select enrichment type</Typography>
							<TextField
								id="add-on-select"
								variant="filled"
								select
								label="Enrichment"
								disabled={true}
								value={addOnType || ''}
								InputProps={{ className: addOnType ? classes.selected : '' }}>
								<MenuItem key={addOnType} value={addOnType}>
									<Typography style={{ textTransform: 'capitalize' }}>
										{addOnType?.name}
									</Typography>
								</MenuItem>
							</TextField>
						</div>
					) : (
						<Typography>No Facility Enrichment</Typography>
					)}
					{addOnType && addOnType === 'treat' ? (
						<div className={classes.fieldWrapper}>
							<Typography variant="overline">Which Treat</Typography>
							<TextField
								id="treat-select"
								variant="filled"
								select
								label="Treat"
								value={treat || ''}
								onChange={(e) => updateTreat(e.target.value)}
								InputProps={{ className: treat ? classes.selected : '' }}
								error={treatError}
								helperText={treatError ? 'Please select a treat.' : null}>
								{renderSelectedAddOns()}
							</TextField>
						</div>
					) : null}
					{addOnType && visit.type !== 'grooming' ? (
						<div className={classes.fieldWrapper}>
							<Typography variant="overline">for which pets</Typography>
							<FormControl className={classes.select}>
								<InputLabel id="pets-label">Pets</InputLabel>
								<Select
									labelId="pets-label"
									id="pets-select"
									variant="filled"
									value={pet}
									disabled={true}
									label="Pets"
									renderValue={(value) => (
										<MuiThemeProvider theme={selectChip}>
											<Chip key={value._id} label={value.name} className={classes.chip} />
										</MuiThemeProvider>
									)}>
									{pet ? (
										<MenuItem key={pet._id} value={pet}>
											<Checkbox checked={pet} />
											<ListItemText primary={pet.name} />
										</MenuItem>
									) : (
										<MenuItem>No pets</MenuItem>
									)}
								</Select>
							</FormControl>
						</div>
					) : null}
					{addOnType && visit.type !== 'grooming' ? (
						<div className={classes.fieldWrapper}>
							<Typography variant="overline">At this time of day</Typography>
							<TextField
								id="time-select"
								variant="filled"
								select
								label="Time Block"
								value={timeBlock || ''}
								onChange={(e) => setTimeBlock(e.target.value)}
								InputProps={{ className: timeBlock ? classes.selected : '' }}
								error={timeBlockError}
								helperText={timeBlockError ? 'Please select a time block.' : null}>
								{timeBlocks.map((timeBlock) => (
									<MenuItem key={timeBlock} value={timeBlock}>
										{timeBlock}
									</MenuItem>
								))}
							</TextField>
						</div>
					) : null}
					{addOnType ? (
						<div className={classes.fieldWrapper}>
							<Typography variant="overline">Edit Default Pricing</Typography>
							<TextField
								id="price"
								variant="filled"
								label="Price"
								disabled={addOn && addOn.completed}
								onChange={(e) => {
									updatePrice(e.target.value)
									setPriceChanged(true)
								}}
								helperText={'Total price (base cost plus any upcharges)'}
								value={formatNumsToDollars(onlyNumbers(values.price))}
								InputProps={{
									startAdornment: (
										<InputAdornment className={classes.currencyAdornment} position="start">
											$
										</InputAdornment>
									)
								}}
								error={priceError}
							/>
						</div>
					) : null}

					{addon_info?.revenue_type === GROOMING || addon_info?.revenue_type === TRAINING ? (
						<div className={classes.fieldWrapper}>
							<Typography variant="overline">Completed By:</Typography>
							<TextField
								id="add-on-select"
								variant="filled"
								select
								label="Completed by"
								disabled={false}
								value={addOn.staff_id}
								onChange={(e) => {
									updateStaff(e.target.value)
								}}
								InputProps={{ className: addOnType ? classes.selected : '' }}>
								{assigned_staff.map((g) => {
									return (
										<MenuItem key={g._id} value={g._id}>
											<Typography style={{ textTransform: 'capitalize' }}>
												{g?.contact?.last_name}, {g?.contact?.first_name}
											</Typography>
										</MenuItem>
									)
								})}
							</TextField>
						</div>
					) : null}
				</div>
				<Snackbar
					open={isErrorOpen}
					autoHideDuration={6000}
					anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
					onClose={handleCloseError}>
					<Alert onClose={handleCloseError} severity="error">
						{error}
					</Alert>
				</Snackbar>
			</DialogContent>
			<DialogActions className={classes.dialogActions}>
				<div className={classes.actionsWrapper}>
					<Button variant="contained" onClick={saveAddOn} color="secondary">
						Save
					</Button>
					<Button variant="outlined" onClick={() => hideDialog()} color="secondary">
						Cancel
					</Button>
					{canDeliverForQa ? (
						<Button variant="outlined" onClick={deliverEnrichment} color="secondary">
							{addOn?.category === 'meal' ? 'Deliver Meal' : 'Deliver Enrichment'}
						</Button>
					) : null}
				</div>
				<Button
					variant="outlined"
					className={classes.deleteButton}
					onClick={() => removeAddOn()}
					color="secondary">
					Delete Activity
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default EditAddOnDialog
